import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { IMAGE_MAX_SIZES } from '@epicuro-next/ataraxia/image';
import {
  EntityType,
  NewsfeedItemResponseDto,
} from '@epicuro-next/data/engagement/engagement-models';
import { ArticleBaseComponent } from '@epicuro-next/features/engagement/article-common';
import {
  isCommercialList,
  isPowerList,
} from '@epicuro-next/features/engagement/list-utils';
import { MediaType } from '@epicuro-next/models/engagement';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import {
  AnalyticsSource,
  ProductListAnalyticsEvent,
} from '@epicuro-next/platform/event-tracker';
import {
  NAMESPACE_NEWS_FEED,
  NewsfeedConfigurationKeys,
  NewsfeedConfigurations,
  NEWSFEED_CONFIGURATIONS_DEFAULT_VALUES,
} from '@epicuro-next/state/news-feed-state';
import { trackList } from '@epicuro-next/state/product-list-state';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

const BREAKPOINT = '(max-width: 767px)';
@Component({
  selector: 'epc-spotlight-item',
  templateUrl: './spotlight-item.component.html',
  styleUrls: ['./spotlight-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SpotlightItemComponent extends ArticleBaseComponent {
  @HostBinding('class.active')
  public get activeItem() {
    return this.isActive;
  }

  @HostBinding('class.active-spotlight')
  public get activeSpotlight() {
    return this.isActive && this.isLookBook;
  }

  @HostBinding('class.lookbook')
  public get lookBookClass() {
    return this.isLookBook;
  }

  public get isVideo() {
    return (
      (this.article?.type === EntityType.ProductList &&
        this.articleMedias.some(
          (media) => media?.mediaType === MediaType.Video,
        )) ||
      this.isDirectShow
    );
  }

  public get isDirectShow() {
    return (
      this.article?.type === EntityType.ProductList &&
      this.articleMedias.some(
        (media) => media?.mediaType === MediaType.Directshow,
      )
    );
  }

  public get isLookBook() {
    return this.article?.type === EntityType.LookBook;
  }

  private get referenceMedias() {
    return this.article.references?.map((ref) => ref?.media) ?? [];
  }

  public get articleMedias() {
    if (this.article?.media) {
      return [this.article.media];
    }

    return this.referenceMedias;
  }

  public get hasCommercialList() {
    return this.article?.references?.some(isCommercialList);
  }

  public get hasPowerList() {
    return this.article?.references?.some(isPowerList);
  }

  public videoAttributes = {
    controls: false,
    autoplay: false,
    disablePlaceholder: true,
  };

  constructor(private breakpointObserver: BreakpointObserver, store: Store) {
    super(store);
  }

  public imageSizes = IMAGE_MAX_SIZES;
  public url: URL;
  public activateMedia = false;
  public isSmall = this.breakpointObserver.isMatched(BREAKPOINT);
  public showPinsWithHover$$ = new BehaviorSubject(false);
  public isLookBookCollapsed$$ = new BehaviorSubject(true);

  public isShowIndicator$ = this.isLookBookCollapsed$$.pipe(
    map((isCollapsed) => (this.isLookBook && isCollapsed) || !this.isLookBook),
  );

  public showPins$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        NewsfeedConfigurationKeys.AllowPinUnpinSpotlight,
        NewsfeedConfigurations
      >(
        NAMESPACE_NEWS_FEED,
        NewsfeedConfigurationKeys.AllowPinUnpinSpotlight,
        NEWSFEED_CONFIGURATIONS_DEFAULT_VALUES[
          NewsfeedConfigurationKeys.AllowPinUnpinSpotlight
        ],
      ),
    ),
  );

  @Input()
  public isActive = false;

  @Output()
  public pinStateChange: EventEmitter<{
    id: string;
    pin: boolean;
  }> = new EventEmitter();

  public productListAnalyticsEvent = ProductListAnalyticsEvent;
  public analyticsSource = AnalyticsSource;

  @HostListener('mouseenter')
  public onMouseEnter() {
    this.showPinsWithHover$$.next(true);
    this.setActivateMedia(true);
  }

  @HostListener('mouseleave')
  public onMouseLeave() {
    this.showPinsWithHover$$.next(false);
    this.setActivateMedia(false);
  }

  @HostListener('click', ['$event'])
  public onArticleClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.articleClick.emit();

    this.setActivateMedia(false);

    if (this.article?.references?.[0]) {
      this.trackList(this.article?.references?.[0]);
    }
  }

  public get referenceIds() {
    return this.article.type === EntityType.LookBook
      ? this.article.references?.map((item) => item?.id)
      : [];
  }

  public togglePinState(value: boolean) {
    this.pinStateChange.emit({
      id: this.article.id as unknown as string,
      pin: value,
    });
  }

  public setActivateMedia(value: boolean) {
    if (this.isActive) {
      return;
    }
    this.activateMedia = value;
  }

  public trackList(reference: NewsfeedItemResponseDto) {
    if (!reference.id) return;
    this.store.dispatch(
      trackList({
        payload: {
          source: 'spotlight',
          productListId: reference.id,
        },
      }),
    );
  }
}
